import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import SimpleBar from "simplebar-react";
import { requestJson } from "../../helpers/api";
import {
  obtenerSesionContexto,
  SesionContexto,
} from "../../helpers/sesionesScope";

interface SesionActiva {
  id: string;
  usuarioId: number | null;
  nombreUsuario: string | null;
  inicio: string;
  ultimaActividad: string | null;
  ip: string | null;
  metadata: Record<string, unknown> | null;
  dispositivo?: string | null;
}

const buildAvatar = (nombre: string | null | undefined) => {
  const iniciales =
    nombre
      ?.split(" ")
      .filter((parte) => parte.length > 0)
      .map((parte) => parte[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "??";
  return (
    <div
      className={`avatar-title rounded-circle text-uppercase ${
        iniciales === "??"
          ? "bg-soft-secondary text-secondary"
          : "bg-soft-primary text-primary"
      } avatar-xs`}
    >
      {iniciales}
    </div>
  );
};

const describirDispositivo = (texto?: string | null) => {
  if (!texto) return "Dispositivo no identificado";
  if (texto.length <= 60) return texto;
  return `${texto.slice(0, 57)}...`;
};

interface GrupoUsuario {
  usuarioId: number | null;
  nombre: string;
  dispositivos: {
    id: string;
    descripcion: string;
    ultimaActividad: string | null;
    ip: string | null;
    usuarioId: number | null;
  }[];
}

const DirectMessagesDropdown = () => {
  const [open, setOpen] = useState(false);
  const [sesiones, setSesiones] = useState<SesionActiva[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cerrandoId, setCerrandoId] = useState<string | null>(null);
  const [accionError, setAccionError] = useState<string | null>(null);
  const [contexto, setContexto] = useState<SesionContexto>(() =>
    obtenerSesionContexto(),
  );
  const toggle = () => setOpen((prev) => !prev);

  const alcance = contexto.alcance;

  const cargarSesiones = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: "12",
        alcance,
      });
      const data = await requestJson<SesionActiva[]>(
        `/api/usuarios/sesiones-activas?${params.toString()}`,
      );
      setSesiones(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo cargar la lista.",
      );
      setSesiones([]);
    } finally {
      setCargando(false);
      setCerrandoId(null);
      setAccionError(null);
    }
  }, [alcance]);

  useEffect(() => {
    setContexto(obtenerSesionContexto());
  }, []);

  useEffect(() => {
    void cargarSesiones();
    const intervalo = setInterval(() => {
      void cargarSesiones();
    }, 30000);
    return () => clearInterval(intervalo);
  }, [cargarSesiones]);

  const descripcionAlcance = useMemo(() => {
    if (alcance === "todos") {
      return "Mostrando todos los usuarios conectados.";
    }
    if (alcance === "sucursal") {
      return "Sesiones activas de mis sucursales asignadas.";
    }
    return "Sólo tu sesión activa.";
  }, [alcance]);

  const gruposUsuarios: GrupoUsuario[] = useMemo(() => {
    const mapa = new Map<string, GrupoUsuario>();
    sesiones.forEach((item) => {
      if (alcance === "propia" && contexto.usuarioId) {
        if (Number(item.usuarioId) !== Number(contexto.usuarioId)) {
          return;
        }
      }
      const clave =
        typeof item.usuarioId === "number" && !Number.isNaN(item.usuarioId)
          ? `u-${item.usuarioId}`
          : `n-${item.nombreUsuario ?? item.id}`;
      if (!mapa.has(clave)) {
        mapa.set(clave, {
          usuarioId: item.usuarioId,
          nombre: item.nombreUsuario ?? "Usuario sin nombre",
          dispositivos: [],
        });
      }
      const grupo = mapa.get(clave)!;
      grupo.dispositivos.push({
        id: item.id,
        descripcion: describirDispositivo(item.dispositivo),
        ultimaActividad: item.ultimaActividad,
        ip: item.ip,
        usuarioId: item.usuarioId ?? null,
      });
    });
    return Array.from(mapa.values());
  }, [sesiones, alcance, contexto.usuarioId]);

  const totalDispositivos = useMemo(
    () =>
      gruposUsuarios.reduce(
        (acum, grupo) => acum + grupo.dispositivos.length,
        0,
      ),
    [gruposUsuarios],
  );

  const puedeCerrarDispositivo = (usuarioId: number | null) => {
    if (contexto.permisos.includes("usuarios.cerrar-sesion")) {
      return true;
    }
    return Boolean(
      contexto.usuarioId &&
        usuarioId &&
        Number(contexto.usuarioId) === Number(usuarioId),
    );
  };

  const cerrarSesionActual = async () => {
    try {
      await requestJson("/api/auth/logout", { method: "POST" });
    } catch (logoutError) {
      console.error("Error al cerrar la sesión actual", logoutError);
    } finally {
      sessionStorage.removeItem("authUser");
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  };

  const cerrarDispositivo = async (id: string, usuarioId: number | null) => {
    if (!puedeCerrarDispositivo(usuarioId)) {
      setAccionError("No tenés permisos para cerrar esta sesión.");
      return;
    }
    if (contexto.sessionId && contexto.sessionId === id) {
      await cerrarSesionActual();
      return;
    }
    setCerrandoId(id);
    setAccionError(null);
    try {
      await requestJson(`/api/usuarios/sesiones/${id}`, {
        method: "DELETE",
      });
      await cargarSesiones();
    } catch (err) {
      setAccionError(
        err instanceof Error
          ? err.message
          : "No se pudo cerrar la sesión seleccionada.",
      );
      setCerrandoId(null);
    }
  };

  return (
    <Dropdown
      isOpen={open}
      toggle={toggle}
      className="topbar-head-dropdown ms-1 header-item"
    >
      <DropdownToggle
        type="button"
        tag="button"
        className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
      >
        <i className="ri-notification-3-line fs-22"></i>
        {totalDispositivos > 0 && (
          <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-success">
            {totalDispositivos}
          </span>
        )}
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
        <div className="dropdown-head bg-success bg-pattern rounded-top">
          <div className="p-3">
            <h6 className="m-0 fs-16 fw-semibold text-white">
              Usuarios en línea
            </h6>
            <p className="mb-0 mt-1 text-white-50 fs-13">
              {totalDispositivos > 0
                ? `${totalDispositivos} dispositivos activos`
                : "Sin sesiones activas"}
            </p>
            <small className="text-white-50 d-block">{descripcionAlcance}</small>
          </div>
        </div>
        <SimpleBar style={{ maxHeight: "300px" }}>
          <ul className="list-group list-group-flush mb-0">
            {cargando && (
              <li className="list-group-item text-center text-muted py-4">
                Actualizando lista...
              </li>
            )}
            {error && !cargando && (
              <li className="list-group-item text-center text-danger py-4">
                {error}
              </li>
            )}
            {accionError && !cargando && (
              <li className="list-group-item text-center text-danger py-3">
                {accionError}
              </li>
            )}
            {!cargando && !error && gruposUsuarios.length === 0 && (
              <li className="list-group-item text-center text-muted py-4">
                Nadie está conectado ahora mismo.
              </li>
            )}
            {!cargando &&
              !error &&
              gruposUsuarios.map((grupo) => (
                <li
                  key={`${grupo.usuarioId ?? grupo.nombre}`}
                  className="list-group-item d-flex align-items-center gap-3"
                >
                  <div className="flex-shrink-0">
                    {buildAvatar(grupo.nombre)}
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-0">{grupo.nombre}</h6>
                    <small className="text-muted d-block">
                      Dispositivos activos: {grupo.dispositivos.length}
                    </small>
                    <ul className="list-unstyled mb-0 mt-2 text-muted small">
                      {grupo.dispositivos.map((dispositivo) => (
                        <li
                          key={dispositivo.id}
                          className="d-flex justify-content-between align-items-center gap-2"
                        >
                          <div>
                            {dispositivo.descripcion}
                            {dispositivo.ip ? ` · IP ${dispositivo.ip}` : ""}
                            {dispositivo.ultimaActividad && (
                              <span>
                                {" "}
                                · Ping{" "}
                                {new Date(
                                  dispositivo.ultimaActividad,
                                ).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                          {puedeCerrarDispositivo(dispositivo.usuarioId) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-soft-danger"
                              disabled={cerrandoId === dispositivo.id}
                              onClick={() =>
                                cerrarDispositivo(
                                  dispositivo.id,
                                  dispositivo.usuarioId,
                                )
                              }
                            >
                              {cerrandoId === dispositivo.id
                                ? "Cerrando..."
                                : "Cerrar"}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
              </li>
            ))}
          </ul>
        </SimpleBar>
      </DropdownMenu>
    </Dropdown>
  );
};

export default DirectMessagesDropdown;
